import { useMutation, useQueryClient } from '@tanstack/vue-query'
import type { MaybeRef } from '@vueuse/core'
import { TanStackQueryIds } from '../../query/query-ids'
import { type TagData, updateTag } from '~~/src/api/tags'
import type { GetArg } from '~~/src/types/utils'

type FnType = typeof updateTag
type Args = GetArg<FnType>
type ReturnData = Awaited<ReturnType<FnType>>

export interface UpdateTagMutateArgs {tagId: Args[0]; tag: Args[1]; userId?: MaybeRef<string | null | undefined>; elementalId?: MaybeRef<string | null | undefined>}

export const useUpdateTagMutation = () => {
  const queryClient = useQueryClient()
  return useMutation<ReturnData, Error, UpdateTagMutateArgs>({
    mutationKey: ['update-tag'],
    mutationFn: async ({ tag, tagId }) => {
      return await updateTag(tagId, tag)
    },
    onMutate({ tag, userId, elementalId }) {
      queryClient.setQueriesData<TagData[]>({
        queryKey: ['user-tags', userId],
        exact: false,
        type: 'all',
      }, (old) => {
        if (!old)
          return old
        return old.map(item => tag.id === item.id ? { ...item, ...tag } : item)
      })
      if (elementalId) {
        queryClient.setQueriesData<TagData[]>({
          queryKey: [TanStackQueryIds.ELEMENTAL_TAGS, elementalId],
          exact: false,
          type: 'all',
        }, (old) => {
          if (!old)
            return old
          return old.map(item => tag.id === item.id ? { ...item, ...tag } : item)
        })
      }
    },
    onError: (_, { userId, elementalId }) => {
      queryClient.refetchQueries({
        queryKey: ['user-tags', userId],
        exact: false,
        type: 'all',
      })
      if (elementalId) {
        queryClient.refetchQueries({
          queryKey: [TanStackQueryIds.ELEMENTAL_TAGS, elementalId],
          exact: false,
          type: 'all',
        })
      }
    },
    onSuccess: (_, { userId }) => {
      queryClient.invalidateQueries({
        queryKey: ['user-tags', userId],
        exact: false,
        type: 'all',
      })
    },
  })
}
